import React, { useState, useEffect, useCallback } from 'react';
import { apiGet } from '../api';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Mail, MessageSquare, Phone, Bell, 
  CheckCircle, XCircle, Clock, AlertCircle,
  RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '../components/ui/button';

const CommunicationLogPage = () => {
  const { api } = useAuth();
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    channel: 'all',
    status: 'all'
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const query = { limit: '100' };
      if (filters.channel !== 'all') query.channel = filters.channel;
      if (filters.status !== 'all') query.log_status = filters.status;

      const [logs, stats] = await Promise.all([
        apiGet('/api/studio/communication-log', { query }),
        apiGet('/api/studio/communication-log/stats'),
      ]);

      setLogs(logs);
      setStats(stats);
    } catch (e) {
      console.error('Failed to fetch communication log:', e);
      toast.error('Failed to load communication log');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getChannelIcon = (channel) => {
    switch (channel) {
      case 'email': return <Mail className="w-4 h-4" />;
      case 'sms': return <Phone className="w-4 h-4" />;
      case 'whatsapp': return <MessageSquare className="w-4 h-4" />;
      case 'push': return <Bell className="w-4 h-4" />;
      default: return <Mail className="w-4 h-4" />;
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'delivered':
        return <Badge className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Delivered</Badge>;
      case 'sent':
        return <Badge className="bg-blue-500"><Clock className="w-3 h-3 mr-1" />Sent</Badge>;
      case 'failed':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Failed</Badge>;
      case 'bounced':
        return <Badge variant="destructive"><AlertCircle className="w-3 h-3 mr-1" />Bounced</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  // Iter-46an: providers (SMTP2Go, ClickSend, Brevo) report `sent` as soon
  // as they accept the message. Only studios with delivery webhooks wired
  // up ever see rows transition to `delivered`. Treat both as successful
  // provider acceptance so the success rate isn't 0% for the common case
  // of no webhook integration.
  const getTotalStats = (channel) => {
    if (!stats || !stats[channel]) return { total: 0, success: 0 };
    const s = stats[channel];
    const sent = s.sent || 0;
    const delivered = s.delivered || 0;
    const failed = s.failed || 0;
    return { total: sent + delivered + failed, success: sent + delivered };
  };

  return (
    <DashboardLayout>
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">Communication Log</h1>
          <p className="text-muted-foreground">View sent notifications from the last 10 days</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          {['email', 'sms', 'whatsapp', 'push'].map((channel) => {
            const { total, success } = getTotalStats(channel);
            const rate = total > 0 ? Math.round((success / total) * 100) : 0;
            return (
              <Card key={channel}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2 capitalize">
                    {getChannelIcon(channel)}
                    {channel === 'sms' ? 'SMS' : channel}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{total}</div>
                  <p className="text-xs text-muted-foreground">
                    {success} successful ({rate}% success rate)
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="py-4">
          <div className="flex gap-4">
            <div className="w-48">
              <Select
                value={filters.channel}
                onValueChange={(v) => setFilters({ ...filters, channel: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by channel" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Channels</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="push">Push Notification</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-48">
              <Select
                value={filters.status}
                onValueChange={(v) => setFilters({ ...filters, status: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Log Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Communications</CardTitle>
          <CardDescription>
            {logs.length} messages in the last 10 days
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No communications found</p>
              <p className="text-sm">Messages will appear here when sent</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Subject/Content</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getChannelIcon(log.channel)}
                        <span className="capitalize">{log.channel}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{log.user_name}</div>
                        <div className="text-sm text-muted-foreground">{log.user_email}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {(log.message_type || 'unknown').replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {log.subject || log.content_preview}
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(log.status)}
                      {log.error_message && (
                        <div className="text-xs text-destructive mt-1">{log.error_message}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {log.sent_at ? format(new Date(log.sent_at), 'MMM d, HH:mm') : '-'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
    </DashboardLayout>
  );
};

export default CommunicationLogPage;
